import React, { Component } from "react";
import { Table } from "tabler-react";

/**
 * TablaSimplificadaTerrestre
 * Tabla simplificada ("Antigüedad del movimiento") para las plataformas
 * 820 (NMP), 2074 (Firstcash), 2075 (Monte Pio Luz Saviño).
 *
 * Columnas:
 *  - ID Movimiento        -> elemento.id
 *  - Estatus principal    -> elemento.idRuta.nombre
 *  - Última actualización -> elemento.idRutaFecha (hora de alta del estatus principal)
 *  - Días sin actualización -> derivado de idRutaFecha (badge por rango de color)
 *
 * Filtros (botones de rango, con toggle):
 *  - "reciente"  : 1 a 5 días
 *  - "seguimiento": 6 a 15 días
 *  - "prioritaria": 16 días o más
 *  Sin filtro activo => se muestran todos los movimientos.
 *
 * Props (datosForm):
 *  @data        Lista de movimientos ya filtrados por plataforma (this.state.elementos)
 *  @cerrarModal Función para cerrar el modal (la inyecta ModalLay)
 */
class TablaSimplificadaTerrestre extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // null = sin filtro; "reciente" | "seguimiento" | "prioritaria"
      filtroRango: null,
    };
  }

  // Parsea idRutaFecha ("yyyy-mm-dd hh:mm:ss") a Date; tolera null/ISO.
  parseFecha = (valor) => {
    if (!valor) return null;
    const d = new Date(valor);
    return isNaN(d.getTime()) ? null : d;
  };

  // Días completos transcurridos desde la fecha hasta hoy.
  diasSinActualizacion = (fecha) => {
    if (!fecha) return null;
    const ms = Date.now() - fecha.getTime();
    if (ms < 0) return 0;
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  // Clave de rango a partir de los días.
  rangoKey = (dias) => {
    if (dias === null) return null;
    if (dias <= 5) return "reciente";
    if (dias <= 15) return "seguimiento";
    return "prioritaria";
  };

  // Rango de antigüedad -> color + leyenda.
  rangoDias = (dias) => {
    const key = this.rangoKey(dias);
    if (key === "reciente") {
      return { color: "#1f8a3b", bg: "#e6f4ea", texto: "Actualización reciente" };
    }
    if (key === "seguimiento") {
      return { color: "#b06f00", bg: "#fef7e0", texto: "Requiere seguimiento" };
    }
    if (key === "prioritaria") {
      return { color: "#c5221f", bg: "#fce8e6", texto: "Atención prioritaria" };
    }
    return { color: "#9aa0a6", bg: "#f1f3f4", texto: "Sin dato" };
  };

  // Toggle del filtro por rango.
  toggleFiltro = (key) => {
    this.setState((prev) => ({
      filtroRango: prev.filtroRango === key ? null : key,
    }));
  };

  render() {
    const todos = Array.isArray(this.props.data) ? this.props.data : [];
    const { filtroRango } = this.state;

    // Aplica filtro por rango (si hay uno activo).
    const data = filtroRango
      ? todos.filter((elemento) => {
          const dias = this.diasSinActualizacion(
            this.parseFecha(elemento.idRutaFecha)
          );
          return this.rangoKey(dias) === filtroRango;
        })
      : todos;

    const botones = [
      {
        key: "reciente",
        label: "1 a 5 días",
        color: "#1f8a3b",
        bg: "#e6f4ea",
      },
      {
        key: "seguimiento",
        label: "6 a 15 días",
        color: "#b06f00",
        bg: "#fef7e0",
      },
      {
        key: "prioritaria",
        label: "16 días o más",
        color: "#c5221f",
        bg: "#fce8e6",
      },
    ];

    return (
      <div>
        {/* Botones de filtro por rango de antigüedad (toggle) */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
          {botones.map((b) => {
            const activo = filtroRango === b.key;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => this.toggleFiltro(b.key)}
                style={{
                  cursor: "pointer",
                  padding: "6px 14px",
                  borderRadius: "16px",
                  border: activo ? `2px solid ${b.color}` : "1px solid #dadce0",
                  backgroundColor: activo ? b.bg : "#ffffff",
                  color: activo ? b.color : "#5f6368",
                  fontSize: "85%",
                  fontWeight: 600,
                }}
              >
                <span style={{ color: b.color, marginRight: "6px" }}>●</span>
                {b.label}
              </button>
            );
          })}
          {filtroRango && (
            <button
              type="button"
              onClick={() => this.setState({ filtroRango: null })}
              style={{
                cursor: "pointer",
                padding: "6px 14px",
                borderRadius: "16px",
                border: "1px solid #dadce0",
                backgroundColor: "#ffffff",
                color: "#5f6368",
                fontSize: "85%",
              }}
            >
              Limpiar filtro
            </button>
          )}
        </div>

        <Table
          cards={true}
          striped={true}
          responsive={true}
          className="table-vcenter"
        >
          <Table.Header>
            <Table.ColHeader>ID Movimiento</Table.ColHeader>
            <Table.ColHeader>Estatus principal</Table.ColHeader>
            <Table.ColHeader>Última actualización</Table.ColHeader>
            <Table.ColHeader>Días sin actualización</Table.ColHeader>
          </Table.Header>
          <Table.Body>
            {data.length === 0 ? (
              <Table.Row>
                <Table.Col colSpan={4}>
                  <div style={{ textAlign: "center", color: "#9aa0a6" }}>
                    {filtroRango
                      ? "Sin movimientos en este rango."
                      : "Sin movimientos para mostrar."}
                  </div>
                </Table.Col>
              </Table.Row>
            ) : (
              data.map((elemento, index) => {
                const estatus =
                  elemento.idRuta && elemento.idRuta.nombre
                    ? elemento.idRuta.nombre
                    : "—";
                const fecha = this.parseFecha(elemento.idRutaFecha);
                const dias = this.diasSinActualizacion(fecha);
                const rango = this.rangoDias(dias);

                return (
                  <Table.Row key={elemento.id != null ? elemento.id : index}>
                    <Table.Col>{elemento.id != null ? elemento.id : "—"}</Table.Col>
                    <Table.Col>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "12px",
                          backgroundColor: "#eef2ff",
                          color: "#3b5bdb",
                          fontSize: "80%",
                          fontWeight: 600,
                        }}
                      >
                        {estatus}
                      </span>
                    </Table.Col>
                    <Table.Col>
                      {elemento.idRutaFecha ? elemento.idRutaFecha : "—"}
                    </Table.Col>
                    <Table.Col>
                      <span
                        title={rango.texto}
                        style={{
                          padding: "2px 10px",
                          borderRadius: "12px",
                          backgroundColor: rango.bg,
                          color: rango.color,
                          fontSize: "80%",
                          fontWeight: 600,
                        }}
                      >
                        {dias === null
                          ? "—"
                          : dias === 1
                          ? "1 día"
                          : dias + " días"}
                      </span>
                    </Table.Col>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>

        {/* Referencia de rangos */}
        <div
          style={{
            marginTop: "12px",
            padding: "10px 14px",
            border: "1px solid #e9ecef",
            borderRadius: "8px",
            fontSize: "80%",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "6px" }}>
            Referencia de rangos
          </div>
          <div>
            <span style={{ color: "#1f8a3b", fontWeight: 700 }}>●</span> 1 a 5
            días&nbsp;&nbsp;Actualización reciente
          </div>
          <div>
            <span style={{ color: "#b06f00", fontWeight: 700 }}>●</span> 6 a 15
            días&nbsp;&nbsp;Requiere seguimiento
          </div>
          <div>
            <span style={{ color: "#c5221f", fontWeight: 700 }}>●</span> 16 días o
            más&nbsp;&nbsp;Atención prioritaria
          </div>
          <div style={{ marginTop: "6px", color: "#9aa0a6" }}>
            La antigüedad se calcula con base en la fecha de la última
            actualización del estatus principal.
          </div>
        </div>
      </div>
    );
  }
}

export default TablaSimplificadaTerrestre;
